import { syncPriceToLeg } from "./CPSettingsGrid";
import { Button } from "@/components/UI/Button";
import InfoTooltip from "@/components/UI/InfoTooltip";
import { determineNotationCurrencySymbol } from "@/lib/currencySymbolHelper";
import {
  returnDecimalFormatting,
  formatValueWithNotation,
  isCurrencyNotation,
  isPriceNotation,
} from "@/lib/globalFormatterHelper";
import {
  containsHyphenInQuote,
  convertToDollarFraction,
  formatDollarFractionString,
  formatDollarFractionWithZValueText,
  regexCheckFractionQuote,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { validateDollarFractionInput } from "@/lib/businessRules/dollarFractionInputValidation";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import { FlashKeyframes, getInputFlashStyles } from "@/lib/styles/flashStyles";
import tradeBlockDataStore from "@/store/tradeBlockDataStore";
import useTableUIStore from "@/store/tableUIStore";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore, {
  ITradeSettingsStore,
} from "@/store/tradeSettingsStore";
import {
  setActiveToggle,
  setNumericSetting,
  setTradeSettingsToOneWayDirectionShown,
} from "@/store/tradeSettingsStoreACTIONS";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import HoverWarningMessage from "../../../../UI/HoverWarningMessage";
import { IdefaultExpandedRowContext } from "../../ActionSummaryTableHelperFunctions";
import { IToggleButton } from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";

import {
  getFontSizeClass,
  getButtonSize,
} from "../../helper/actionSummaryHelper";
import { blocksContainNPV } from "../../helper/tradeSettingsHelper/unwindSideRule";

// Moved outside component to prevent re-creation on every render (which causes input focus loss)
const SettingsHTML: React.FC<{
  heading: string;
  children: React.ReactNode;
  colId?: string;
  isHidden?: boolean;
  fontSizeClass?: string;
  isLargeFont?: boolean;
  infoMessage?: string;
}> = ({
  heading,
  children,
  colId,
  isHidden,
  fontSizeClass = "text-xs",
  isLargeFont = false,
  infoMessage,
}) => {
  return (
    <div className={`${isHidden ? "hidden" : ""} flex w-full items-center`}>
      <div
        className={`${colId} ${isLargeFont ? "min-w-[100px]" : "min-w-[90px]"} ${fontSizeClass}`}
      >
        {heading}:
      </div>
      <div className="relative flex h-full min-w-0 flex-1 items-center">
        {children}
      </div>
      {infoMessage && (
        <div className="flex-shrink-0">
          <InfoTooltip id={`${colId}-info`} message={infoMessage} />
        </div>
      )}
    </div>
  );
};

/**
 * Formats a store value with notation-based rounding for display.
 * Returns empty string if value is undefined.
 * Uses "$" for rounding when notation is a resolved currency symbol (£, €, AUD, etc.)
 * For $fr: displays fraction strings as-is, converts numbers to fraction format.
 */
function formatStoreValueForDisplay(
  value: number | string | undefined,
  notation?: NotationToDecimalValueType | string,
): string {
  if (value === undefined) return "";
  // $fr: if value is already a fraction string, display as-is
  if (
    notation === "$fr" &&
    typeof value === "string" &&
    containsHyphenInQuote(value)
  ) {
    return formatDollarFractionString(value);
  }
  // $fr: if value is a number, convert to fraction format
  if (notation === "$fr" && typeof value === "number") {
    return formatDollarFractionString(convertToDollarFraction(value));
  }
  const numValue = typeof value === "string" ? Number(value) : value;
  if (isNaN(numValue)) return value.toString();
  // Use "$" for decimal place calculation when notation is a resolved currency symbol
  const roundingNotation =
    notation && isCurrencyNotation(notation)
      ? ("$" as NotationToDecimalValueType)
      : (notation as NotationToDecimalValueType | undefined);
  const rounded = returnDecimalFormatting(numValue, roundingNotation, true);
  return rounded || numValue.toString();
}

// Input component that manages local state and commits on blur
// Exported for reuse in CPSettingsGrid (Order settings row)
export const NumericSettingInput: React.FC<{
  storeValue: number | string | undefined;
  id: string;
  disabled: boolean;
  onCommit: (value: number | string | undefined) => void;
  esid: string;
  fieldName: keyof ITradeSettingsStore;
  notation?: NotationToDecimalValueType | string;
  fontSizeClass?: string;
}> = ({
  storeValue,
  id,
  disabled,
  onCommit,
  esid,
  fieldName,
  notation,
  fontSizeClass = "text-xs",
}) => {
  const isDollarFraction = notation === "$fr";
  const [localValue, setLocalValue] = useState<string>(
    formatStoreValueForDisplay(storeValue, notation),
  );

  // Subscribe to flash state from store
  const orderSettingFieldsToFlash = tradeSettingsStore(
    (state) => state.orderSettingFieldsToFlash,
  );

  // Check if this field should flash
  const shouldFlash = orderSettingFieldsToFlash.some(
    (f) => f.esid === esid && f.field === fieldName,
  );

  const [isFlashing, setIsFlashing] = useState(false);

  // Handle flash effect
  useEffect(() => {
    if (shouldFlash) {
      setIsFlashing(true);
    } else {
      setIsFlashing(false);
    }
  }, [shouldFlash]);

  // Sync local state when store value changes externally
  // Apply notation-based rounding for display
  useEffect(() => {
    setLocalValue(formatStoreValueForDisplay(storeValue, notation));
  }, [storeValue, notation]);

  const handleBlur = () => {
    if (localValue === "") {
      onCommit(undefined);
      return;
    }
    // $fr: validate and convert to fraction format on blur
    if (isDollarFraction) {
      if (!regexCheckFractionQuote({ newValue: localValue })) {
        setLocalValue("");
        onCommit(undefined);
        return;
      }
      // If user typed a decimal, convert to fraction
      if (!containsHyphenInQuote(localValue)) {
        const numVal = Number(localValue);
        if (!isNaN(numVal)) {
          const fractionStr = formatDollarFractionString(
            convertToDollarFraction(numVal),
          );
          setLocalValue(fractionStr);
          onCommit(fractionStr);
          return;
        }
      }
      // User typed a fraction — just pad formatting
      const formatted = formatDollarFractionString(localValue);
      setLocalValue(formatted);
      onCommit(formatted);
      return;
    }
    // Non-$fr: standard numeric handling
    const numValue = Number(localValue);
    if (!isNaN(numValue)) {
      onCommit(numValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const inputFlashStyle = getInputFlashStyles(isFlashing);

  const [isFocused, setIsFocused] = useState(false);

  // Display value: show notation when blurred and has value
  // Currency notations prepend (e.g., "$4.50"), non-currency append (e.g., "4.50 %")
  // $fr: convert Z-value digit to Unicode fraction (e.g., "116-023" → "116-02⅜") — no suffix
  const displayValue =
    !isFocused && localValue !== "" && notation
      ? isDollarFraction && containsHyphenInQuote(localValue)
        ? formatDollarFractionWithZValueText(localValue)
        : formatValueWithNotation(localValue, notation)
      : localValue;

  return (
    <>
      <FlashKeyframes isFlashing={isFlashing} />
      <div className="flex items-center">
        <input
          type="text"
          id={id}
          inputMode="decimal"
          className={clsx(
            `max-w-28 mr-1 h-[26px] w-28 rounded-md border bg-transparent px-2 text-center ${fontSizeClass} dark:text-gray-200`,
            disabled && "cursor-not-allowed opacity-50",
            !isFlashing && "border-gray-300 dark:border-gray-600",
          )}
          style={inputFlashStyle}
          value={displayValue}
          // value={"hi"}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true);
            // Use setTimeout to ensure value updates before selection
            setTimeout(() => e.target.select(), 0);
          }}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "") {
              setLocalValue(val);
              return;
            }
            // $fr: use dollar fraction input validation
            if (isDollarFraction) {
              if (validateDollarFractionInput(val)) {
                setLocalValue(val);
              }
              return;
            }
            // Non-$fr: only allow numeric input (digits, decimal point, minus)
            if (/^-?\d*\.?\d*$/.test(val)) {
              setLocalValue(val);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
            handleBlur();
          }}
          onKeyDown={handleKeyDown}
          placeholder={
            notation
              ? isPriceNotation(notation)
                ? "price"
                : String(notation).trim()
              : ""
          }
        />
      </div>
    </>
  );
};

export interface ISelectPanelSettings {
  data: any;
  expandedRowsContext: IdefaultExpandedRowContext[];
  setExpandedRowsContext: React.Dispatch<
    React.SetStateAction<IdefaultExpandedRowContext[]>
  >;
  setExpandedRowsContextMostRecentlyUpdated: React.Dispatch<
    React.SetStateAction<string>
  >;
  getAccessTokenSilently: Function;
  containsClose: boolean;
}

function returnHiddenClassesForSettings(
  currentId: string,
  thisTradeSettings: ITradeSettingsStore,
): boolean {
  if (thisTradeSettings && currentId == "releaseDirectionType") {
    if (
      thisTradeSettings.quoteType.find((type) => type.name == "One-Way")
        ?.active == true
    ) {
      return true;
    }
  }
  return false;
}

const SelectPanelSettings = (props: ISelectPanelSettings) => {
  const {
    data,
    containsClose,
  } = props;

  const dataStrategyType = data.strategyType;
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);
  const dynamicButtonSize = getButtonSize(fontSizeSetting, "sm");

  // Trade Settings - from store (derived directly to avoid extra re-render).
  const thisTradeSettings = tradeSettingsStore(
    (state) =>
      state.tradeSettingsData.find((t) => t.esid === data.esid)?.settings,
  );

  // Get notation from ES level (notationES) - available from start
  const rawNotation = (data?.notationES || undefined) as
    | NotationToDecimalValueType
    | undefined;

  // Resolve "$" notation to actual currency symbol (e.g., "£", "€", "AUD ")
  // Subscribe to tradeData for reactive updates when trade data changes
  const tradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade = tradeData.find(
    (trade: ExecutionStrategy) => trade.esid === data.esid,
  );
  const notation = thisTrade
    ? determineNotationCurrencySymbol(thisTrade, rawNotation)
    : rawNotation;

  // const [showUnwindButton, setShowUnwindButton] = useState(false);
  // const [containsClose, setContainsClose] = useState(false);

  // NPV - Recoupon information. Recoupon is display-only and lives in
  // SelectPanelUnwindIndicator, so only the NPV flag is tracked here.
  const [NPVLegInTrade, setNPVLegInTrade] = useState(false);

  const allBlockInfo = tradeBlockDataStore((state) => state.tradeBlockData);

  // Update the trade to contain either NPV or Recoupon based on the legs endpoint.
  useEffect(() => {
    const thisBlockInfo = allBlockInfo.find(
      (trades) => trades.esid == data.esid,
    );
    if (thisBlockInfo) {
      if (thisBlockInfo.blocks && thisBlockInfo.blocks.length > 0) {
        const containsNPV = blocksContainNPV(thisBlockInfo.blocks);
        setNPVLegInTrade(containsNPV);
        // Only NPV forces One-Way + Direction SHOW - see unwindSideRule.
        if (containsNPV) {
          setTradeSettingsToOneWayDirectionShown({ esid: data.esid });
        }
      }
    }
  }, [allBlockInfo]);

  if (thisTradeSettings == undefined) {
    return; // Early return if no trade settings:
  }

  interface ISettingsButtonCluster {
    setting: IToggleButton[]; // Not all settings types will be put through this map.
    settingField: keyof ITradeSettingsStore;
    heading: string;
    id: string;
    showOnTradeTypes?: string[]; // Which trade types show this setting (e.g., ["RFQ", "Order"])
    showOnStrategyTypes?: string[]; // Which structure types show this setting (e.g., ["Outright", "Structure"])
    inputField?: keyof ITradeSettingsStore; // Optional input field before buttons
    controlledBy?: keyof ITradeSettingsStore; // Which parent setting controls this row's enabled/disabled state
    enabledWhenValue?: string[]; // Which value(s) of the parent setting should keep this row enabled
    disabledMessage?: string; // Message to show on hover when row is disabled
    infoMessage?: string;
  }

  const activeTradeType = thisTradeSettings.tradeTypes.find(
    (t) => t.active,
  )?.name;

  const settingsButtonCluster: ISettingsButtonCluster[] = [
    // quoteType (Side), releaseDirection (Direction), and autoAffirm moved to CPSettingsGrid DEFAULTS section
    // Order settings
    // 15426 only on Structures
    {
      setting: thisTradeSettings!.orderSetLevelStructure,
      settingField: "orderSetLevelStructure",
      heading: "Set level",
      id: "orderSetLevelStructure",
      showOnTradeTypes: ["Order"],
      showOnStrategyTypes: ["Structure"],
    },
    {
      setting: thisTradeSettings!.orderSpreadType,
      settingField: "orderSpreadType",
      heading: "Spread",
      id: "orderSpreadType",
      showOnTradeTypes: ["Order"],
      showOnStrategyTypes: ["Structure"],
      inputField: "orderSpreadValue",
      controlledBy: "orderSetLevelStructure",
      enabledWhenValue: ["Spread / Legs"],
      disabledMessage: "* Only used when 'Set level' is 'Spread / Legs'",
    },
    // 15426 only on Structures
    {
      setting: thisTradeSettings!.orderSetLevelOutright,
      settingField: "orderSetLevelOutright",
      heading: "Set Level",
      id: "orderSetLevelOutright",
      showOnTradeTypes: ["Order"],
      showOnStrategyTypes: ["Outright"],
    },
    {
      setting: thisTradeSettings!.orderPriceType,
      settingField: "orderPriceType",
      heading: "Level",
      id: "orderPriceType",
      showOnTradeTypes: ["Order"],
      showOnStrategyTypes: ["Outright"],
      inputField: "orderPriceValue",
      controlledBy: "orderSetLevelOutright",
      enabledWhenValue: ["Leg"],
      disabledMessage: "* Only used when 'Set Level' is Leg.",
    },

  ];

  // Helper function to check if a row is disabled based on its parent setting
  function isRowDisabled(settingCluster: ISettingsButtonCluster): boolean {
    if (!settingCluster.controlledBy || !settingCluster.enabledWhenValue) {
      return false;
    }
    const controllerSetting = thisTradeSettings![
      settingCluster.controlledBy
    ] as IToggleButton[];
    const activeValue = controllerSetting?.find((t) => t.active)?.name;
    return !settingCluster.enabledWhenValue.includes(activeValue || "");
  }

  // Sync orderPriceValue to leg referencePrice for Outright trades


  function isButtonDisabled(
    settingCluster: ISettingsButtonCluster,
    NPVLegInTrade: boolean,
    buttonType?: IToggleButton,
  ): boolean {
    // Check if the row is disabled by its parent setting
    if (isRowDisabled(settingCluster)) {
      return true;
    }
    if (
      // (settingCluster.settingField == "tradeTypes" && true) || // REMOVE ONCE TRADE TYPE IS ACTIVE
      settingCluster.settingField == "quoteType" &&
      containsClose
    ) {
      return true;
    }
    if (settingCluster.settingField == "quoteType") {
      if (NPVLegInTrade) {
        return true;
      }
      // Disable Two-Way when tradeType is Order
      if (activeTradeType === "Order" && buttonType?.name === "Two-Way") {
        return true;
      }
    }
    return false;
  }

  function returnButtonVarient(
    type: IToggleButton,
    settingCluster: ISettingsButtonCluster,
    NPVLegInTrade: boolean,
  ): "selectionActive" | "selectionDisabled" | "selectionInactive" {
    // Check if the row is disabled by its parent setting
    if (isRowDisabled(settingCluster)) {
      return "selectionDisabled";
    }
    if (type.active) {
      return "selectionActive";
    }
    // if (settingCluster.settingField == "tradeTypes") {
    //   return "selectionDisabled"; // REMOVE ONCE TRADE TYPE IS ACTIVE
    // }
    if (settingCluster.settingField == "quoteType") {
      if (NPVLegInTrade) {
        return "selectionDisabled";
      }
      // Show Two-Way as disabled when tradeType is Order
      if (activeTradeType === "Order" && type.name === "Two-Way") {
        return "selectionDisabled";
      }
    }
    return "selectionInactive";
  }

  return (
    <div
      className={`MainContainer detail-wrapper my-1 flex h-full w-full flex-row justify-start gap-8 pl-2 text-[#404040] dark:text-[#FAFAFA] `}
    >
      <div className="flex flex-col gap-1 transition-all duration-500 ease-in-out">
        {/* <div className="w-full text-center">Settings</div> */}
        {settingsButtonCluster
          .filter((settingCluster) => {
            const matchesTradeType =
              !settingCluster.showOnTradeTypes ||
              settingCluster.showOnTradeTypes.includes(activeTradeType || "");
            const matchesStrategyType =
              !settingCluster.showOnStrategyTypes ||
              settingCluster.showOnStrategyTypes.includes(dataStrategyType); //
            return matchesTradeType && matchesStrategyType;
          })
          .map((settingCluster) => {
            return (
              <SettingsHTML
                heading={settingCluster.heading}
                colId={settingCluster.id}
                key={settingCluster.id}
                fontSizeClass={fontSizeClass}
                isLargeFont={fontSizeSetting === "large"}
                infoMessage={settingCluster.infoMessage}
                isHidden={returnHiddenClassesForSettings(
                  settingCluster.id,
                  thisTradeSettings,
                )}
              >
                <HoverWarningMessage
                  message={settingCluster.disabledMessage || ""}
                  showWhen={
                    isRowDisabled(settingCluster) &&
                    !!settingCluster.disabledMessage
                  }
                >
                  {/* Optional input field before buttons */}
                  {settingCluster.inputField && (
                    <NumericSettingInput
                      storeValue={
                        thisTradeSettings[settingCluster.inputField] as
                          | number
                          | string
                          | undefined
                      }
                      id={settingCluster.id}
                      disabled={isRowDisabled(settingCluster)}
                      esid={data.esid}
                      fieldName={settingCluster.inputField}
                      notation={notation}
                      fontSizeClass={fontSizeClass}
                      onCommit={(value) => {
                        setNumericSetting({
                          esid: data.esid,
                          settingField: settingCluster.inputField!,
                          value: value,
                        });
                        // Sync to leg referencePrice for Outright orders
                        if (settingCluster.inputField === "orderPriceValue") {
                          syncPriceToLeg(data.esid, dataStrategyType, value);
                        }
                      }}
                    />
                  )}
                  {thisTradeSettings &&
                    settingCluster &&
                    settingCluster.setting &&
                    settingCluster.setting.map((type: IToggleButton) => (
                      <Button
                        variantToggle={returnButtonVarient(
                          type,
                          settingCluster,
                          NPVLegInTrade,
                        )}
                        id={type.name}
                        className={clsx(
                          `dark:border-gray-500`,
                          settingCluster.setting.length === 1 &&
                            "rounded-l-md rounded-r-md",
                          settingCluster.setting.length > 1 &&
                            type === settingCluster.setting[0] &&
                            "rounded-l-md",
                          settingCluster.setting.length > 1 &&
                            type ===
                              settingCluster.setting[
                                settingCluster.setting.length - 1
                              ] &&
                            "rounded-r-md",
                          settingCluster.setting.length > 2 &&
                            type !== settingCluster.setting[0] &&
                            type !==
                              settingCluster.setting[
                                settingCluster.setting.length - 1
                              ] &&
                            "border-x",
                        )}
                        size={dynamicButtonSize}
                        key={type.name}
                        onClick={() => {
                          setActiveToggle({
                            esid: data.esid,
                            selectedElement: type, // Generated from the map.
                            stateVariable: settingCluster.setting!,
                            settingField: settingCluster.settingField,
                          });
                        }}
                        disabled={isButtonDisabled(
                          settingCluster,
                          NPVLegInTrade,
                          type,
                        )}
                      >
                        {type.name}
                      </Button>
                    ))}
                </HoverWarningMessage>
              </SettingsHTML>
            );
          })}
      </div>

      {/*  UNWIND SPECIFIC SETTINGS */}
      {/* {showUnwindButton ? (
        <div className="mb-2 flex h-fit gap-5 rounded-lg border p-1 pl-2 dark:border-gray-600">
          <div id="swapLH">
            <div className="flex w-full">
              <div
                data-tooltip-id={"swapSettingsInfo"}
                className="flex h-4 w-4"
              >
                {information}
              </div>
              <div>Swap Settings:</div>
            </div>
            <Button
              variant={"mainOrangeAction"} // was secondaryBlueAction
              id={"SelectPanelUnwind"}
              className="mt-1 w-24 text-xs"
              onClick={() => {
                let currentExpandedRowsContext: IdefaultExpandedRowContext[];
                if (expandedRowsContext) {
                  currentExpandedRowsContext = [...expandedRowsContext];
                } else {
                  currentExpandedRowsContext = [];
                }

                let findSettingsForCurrentESID =
                  currentExpandedRowsContext.find((expandedSetting) => {
                    return expandedSetting.esid == data.esid;
                  });
                if (findSettingsForCurrentESID == undefined) {
                  let newSettings = { ...defaultExpandedRowContext };
                  newSettings.esid = data.esid;
                  newSettings.legsShowing = true;
                  setExpandedRowsContext([
                    ...currentExpandedRowsContext,
                    newSettings,
                  ]);
                } else {
                  let adjustedCurrentSettingsArray =
                    currentExpandedRowsContext.map((settingsObj) =>
                      settingsObj.esid == findSettingsForCurrentESID!.esid
                        ? {
                            ...settingsObj,
                            legsShowing: !settingsObj.legsShowing,
                            editPanelShowing: false,
                            allocationsShowing: false,
                          }
                        : settingsObj,
                    );
                  setExpandedRowsContext(adjustedCurrentSettingsArray);
                }
                setExpandedRowsContextMostRecentlyUpdated(data.esid);
                // Need to get block data for all trade types - May be calling Swaps again.
                getBlocksData({
                  esid: data.esid,
                  getAccessTokenSilently: getAccessTokenSilently,
                });
              }}
              title={"Unwind"}
              size={"button_md_CP"}
            >
              Unwind
            </Button>
          </div>

          <div id="swapRH" className="flex w-full flex-col justify-around">
            <div className="flex w-full">
              <div>Legs:</div>
            </div>

            <div className="flex w-full justify-between">
              <div
                className={`flex h-fit w-fit items-center justify-center gap-[1px] ${NPVLegInTrade ? "" : "text-gray-400"}`}
              >
                <span className="flex">NPV</span>
                <span
                  className={`flex h-5 w-5 ${NPVLegInTrade ? "text-green-500" : "text-gray-400"}`}
                >
                  {NPVLegInTrade ? circleWithTick : circleWithCross}
                </span>
              </div>
              <div
                className={`flex h-fit w-fit items-center justify-center gap-[1px] ${RecouponLegInTrade ? "" : "text-gray-400"}`}
              >
                <span className="flex">Recoupon</span>
                <span
                  className={`flex h-5 w-5 ${RecouponLegInTrade ? "text-green-500" : "text-gray-400"}`}
                >
                  {RecouponLegInTrade ? circleWithTick : circleWithCross}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div></div>
      )}
      {createPortal(
        <Tooltip
          id={"swapSettingsInfo"}
          place="left"
          variant="info"
          className=" z-20"
          clickable={true}
          // openOnClick={true}
          opacity={1}
          style={{
            color: theme == "dark" ? "white" : "black",
            backgroundColor:
              theme == "dark"
                ? cubeCustomColors.cubeBaseDark[700]
                : cubeCustomColors.cubeBase[700],
            margin: "0px",
            padding: "2px",
            paddingLeft: "5px",
            paddingRight: "5px",
          }}
          // offset={}
          border="1px solid gray"
        >
          <div
            id="SwapInfoContainer"
            className="flex w-full max-w-[370px] flex-col gap-2 p-1"
          >
            <div>
              Unwind opens a table to set legs to NPV or Recoupon, showing a
              green tick if applied.
            </div>
            <div>Two way trades can’t be actioned for NPV.</div>
          </div>
        </Tooltip>,
        document.body,
      )} */}
    </div>
  );
};

export default SelectPanelSettings;
